import { requestData, requestPage, type Page, type PageQuery } from '@/lib/api-client';
import type {
  Prescription,
  PrescriptionDetail,
  PrescriptionStatus,
  StructuredDosage,
  Uuid,
} from './types';

export type PrescriptionQuery = PageQuery & {
  /**
   * Several at once, sent as a repeated param. Omit it and the server answers
   * with everything still open — UPLOADED and UNDER_REVIEW — as a query
   * condition rather than as a filter over an already-fetched page.
   */
  status?: readonly PrescriptionStatus[];
};

/**
 * Everything sent to this shop, filtered to what the caller asks for.
 *
 * Scoped by the server to the pharmacies the signed-in staff belong to — a
 * script is assigned a serving shop at upload, from the customer's delivery
 * address. A platform reader with no counter sees the lot.
 */
export function listReviewQueue(query: PrescriptionQuery = {}): Promise<Page<Prescription>> {
  return requestPage<Prescription>('/review-queue', { query });
}

/** How many scripts sit behind each tab. */
export type PrescriptionFacets = {
  total: number;
  byStatus: Partial<Record<PrescriptionStatus, number>>;
};

/**
 * The tab counts, over every script this shop can see.
 *
 * Its own route rather than `/prescriptions/facets`, which scopes by CUSTOMER
 * and is what the admin board reads — pointed at from here it would count the
 * pharmacist's own uploads, which is zero on every tab.
 */
export function getReviewQueueFacets(): Promise<PrescriptionFacets> {
  return requestData<PrescriptionFacets>('/review-queue/facets');
}

export function getPrescription(prescriptionId: Uuid): Promise<PrescriptionDetail> {
  return requestData<PrescriptionDetail>(`/prescriptions/${prescriptionId}`);
}

export type TranscribedLine = StructuredDosage & {
  prescribedName: string;
  medicineId?: Uuid | null;
  strength?: string | null;
  dosage?: string | null;
  frequency?: string | null;
  duration?: string | null;
  quantity?: number | null;
  instructions?: string | null;
};

/**
 * Replaces the transcribed lines wholesale.
 *
 * Not a per-line edit: a half-transcribed script is not a state worth
 * reviewing, and "the lines as read on this pass" is the unit the work
 * actually happens in. Refused once a decision exists, because the decisions
 * point at these rows.
 */
export function setPrescriptionItems(
  prescriptionId: Uuid,
  items: TranscribedLine[],
): Promise<PrescriptionDetail> {
  return requestData<PrescriptionDetail>(`/prescriptions/${prescriptionId}/items`, {
    method: 'PUT',
    body: { items },
  });
}

export type ReviewLine = {
  prescriptionItemId: Uuid;
  decision: 'APPROVED' | 'REJECTED' | 'NEEDS_CLARIFICATION';
  approvedQuantity?: number | null;
  selectedMedicineId?: Uuid | null;
  /** MRP off the box. The server requires it when the shop has none of the medicine on the shelf. */
  mrp?: string | null;
  reason?: string | null;
};

/**
 * The decision, per line. The headline status is DERIVED by the server from
 * these — a reviewer who could set the two independently could publish an
 * approved script whose every line was refused.
 */
export function reviewPrescription(
  prescriptionId: Uuid,
  body: { comments?: string | null; rejectionReason?: string | null; items: ReviewLine[] },
): Promise<PrescriptionDetail> {
  return requestData<PrescriptionDetail>(`/prescriptions/${prescriptionId}/reviews`, {
    method: 'POST',
    body,
  });
}

/**
 * Lowers what was approved on one line, after the decision.
 *
 * Down only — the ceiling is whatever the newest review holds and the server
 * refuses anything above it, naming the figure. It also refuses below what has
 * already been dispensed, so the register still reconciles.
 *
 * Deliberately separate from `reviewPrescription`: this cannot approve, reject
 * or substitute anything. It is the counter correcting a number, not making a
 * clinical decision over again.
 */
export function reduceApprovedQuantity(
  prescriptionId: Uuid,
  body: { prescriptionItemId: Uuid; approvedQuantity: number; reason: string },
): Promise<PrescriptionDetail> {
  return requestData<PrescriptionDetail>(`/prescriptions/${prescriptionId}/approved-quantity`, {
    method: 'PATCH',
    body,
  });
}

/** Withdraws the script. Refused outright once anything has been dispensed. */
export function cancelPrescription(
  prescriptionId: Uuid,
  reason: string,
): Promise<PrescriptionDetail> {
  return requestData<PrescriptionDetail>(`/prescriptions/${prescriptionId}/cancel`, {
    method: 'POST',
    body: { reason },
  });
}
